import React, { useState } from 'react';
import { CustomField } from '../types';
import './CustomFieldsEditor.css';

interface CustomFieldsEditorProps {
  fields: CustomField[];
  onFieldsChange: (fields: CustomField[]) => void;
  onClose: () => void;
}

const FIELD_TYPES: { value: CustomField['field_type']; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'choice', label: 'Choice' },
  { value: 'list', label: 'List' },
  { value: 'number', label: 'Number' },
];

const CustomFieldsEditor: React.FC<CustomFieldsEditorProps> = ({
  fields,
  onFieldsChange,
  onClose,
}) => {
  const [newField, setNewField] = useState<CustomField>({
    name: '',
    description: '',
    field_type: 'text',
  });

  const handleAddField = () => {
    if (!newField.name.trim()) return;
    onFieldsChange([...fields, { ...newField }]);
    setNewField({ name: '', description: '', field_type: 'text' });
  };

  const handleRemoveField = (index: number) => {
    onFieldsChange(fields.filter((_, i) => i !== index));
  };

  return (
    <div className="fields-editor-overlay">
      <div className="fields-editor">
        <div className="fields-editor-header">
          <h3>📋 Custom Response Fields</h3>
          <button onClick={onClose} className="close-button">✕</button>
        </div>

        <p className="fields-description">
          Add custom fields that the AI will populate for each question.
        </p>

        <div className="existing-fields">
          {fields.length === 0 ? (
            <p className="no-fields">No custom fields added yet</p>
          ) : (
            fields.map((field, index) => (
              <div key={index} className="field-item">
                <div className="field-info">
                  <span className="field-name">{field.name}</span>
                  <span className="field-type">{field.field_type}</span>
                </div>
                <button
                  onClick={() => handleRemoveField(index)}
                  className="remove-field"
                >
                  🗑️
                </button>
              </div>
            ))
          )}
        </div>

        <div className="add-field-form">
          <h4>Add New Field</h4>
          <div className="form-row">
            <input
              type="text"
              placeholder="Field name"
              value={newField.name}
              onChange={(e) => setNewField({ ...newField, name: e.target.value })}
            />
            <select
              value={newField.field_type}
              onChange={(e) =>
                setNewField({ ...newField, field_type: e.target.value as CustomField['field_type'] })
              }
            >
              {FIELD_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder="Description (helps AI understand what to generate)"
            value={newField.description}
            onChange={(e) => setNewField({ ...newField, description: e.target.value })}
            className="description-input"
          />
          <button onClick={handleAddField} className="add-button" disabled={!newField.name.trim()}>
            + Add Field
          </button>
        </div>

        <button onClick={onClose} className="done-button">
          Done
        </button>
      </div>
    </div>
  );
};

export default CustomFieldsEditor;

